import MeasureShape from "@/pages/takeoff/context/project-measure/models/measure-shape.ts";
import MeasureType from "@/pages/takeoff/context/project-measure/models/measure-type.ts";
import rgbaToHexAndAlpha from "@/utils/colors/rgba-to-hex-and-alpha.ts";
import shouldConvertFromCentimetersToMeters from "@/pages/takeoff/layouts/measures/layouts/storey-measures/helpers";

interface AddMeasureTypeActionParams {
    formData: FormData;
    predefinedShapes: MeasureShape[];
    projectMeasureTypes: MeasureType[];
    addMeasureType: (measureRepresentationId: string, name: string, dataInformations: string) => Promise<MeasureType | null>;
}

interface AddMeasureTypeActionResponse {
    success?: boolean;
    message?: string;
    addedMeasureType?: MeasureType;
}

const addMeasureTypeAction = async ({formData, predefinedShapes, projectMeasureTypes, addMeasureType}: AddMeasureTypeActionParams) : Promise<AddMeasureTypeActionResponse> => {

    const name = (formData.get('name') as string || '').trim();
    const color = formData.get('color') as string;
    const hasAdditionalConstruction = formData.get('hasAdditionalConstruction') === 'true';
    const measureRepresentationIdFromForm = formData.get('measureRepresentationId') as string;
    const shapeName = formData.get('shape') as string;

    const selectedShape = predefinedShapes.find(s => s.name === shapeName);

    if (name === '' || !selectedShape || !color) {
        return { message: 'Veuillez remplir tous les champs.' };
    }

    let measureRepresentationId: string | undefined = measureRepresentationIdFromForm;
    let dataInformations: Record<string, unknown> = {};

    const { hex, alpha } = rgbaToHexAndAlpha(color);
    const stringAlpha = String(alpha);

    if (hasAdditionalConstruction) {
        const selectedMeasureRepresentation = selectedShape.representations.find(r => r.measureRepresentationId === measureRepresentationIdFromForm);

        if (!selectedMeasureRepresentation) {
            return { message: 'Représentation de métré introuvable.' };
        }

        const dataFormatTypeValues: Record<string, unknown> = {
            color: hex,
            alpha: stringAlpha,
        };

        let allFieldsFilled = true;

        if (selectedMeasureRepresentation.dataFormatType) {
            Object.entries(selectedMeasureRepresentation.dataFormatType)
                .filter(([key]) => key !== 'color' && key !== 'alpha')
                .forEach(([key, type]) => {
                    const val = formData.get(`dataFormatType_${key}`);

                    if (typeof val !== 'string' || val.trim() === '') {
                        allFieldsFilled = false;
                        return;
                    }

                    if (type === 'number') {
                        let numVal = parseFloat(val);
                        if(shouldConvertFromCentimetersToMeters(key))
                            numVal /= 100;
                        dataFormatTypeValues[key] = Number.isNaN(numVal) ? 0 : numVal;
                    } else {
                        dataFormatTypeValues[key] = String(val);
                    }
                });
        }

        if (!allFieldsFilled) {
            return { message: 'Veuillez remplir tous les champs.' };
        }

        dataInformations = dataFormatTypeValues;
    }
    else {
        switch (selectedShape.name) {
            case 'Polyline':
                measureRepresentationId = selectedShape.representations.find(mr => mr.name === 'PolylinePanel')?.measureRepresentationId;
                dataInformations = {color: hex, alpha: stringAlpha, height: 0.005};
                break;

            case 'Point':
                measureRepresentationId = selectedShape.representations.find(mr => mr.name === 'Disk')?.measureRepresentationId;
                dataInformations = {color: hex, alpha: stringAlpha, radius: 0.005};
                break;

            case 'Surface':
                measureRepresentationId = selectedShape.representations.find(mr => mr.name === 'FineSurface')?.measureRepresentationId;
                dataInformations = {color: hex, alpha: stringAlpha };
                break;

            case 'Line':
                measureRepresentationId = selectedShape.representations.find(mr => mr.name === 'LineImprint')?.measureRepresentationId;
                dataInformations = {color: hex, alpha: stringAlpha, thickness: 0.005};
                break;

            default:
                break;
        }
    }

    if (!measureRepresentationId) {
        return { message: 'Représentation de métré introuvable.' };
    }

    if (projectMeasureTypes.find(mt => mt.name === name && mt.measureRepresentationId === measureRepresentationId)) {
        return { message: 'Le type indiqué existe déjà dans votre projet.' };
    }

    try {
        const type = await addMeasureType(measureRepresentationId, name, JSON.stringify(dataInformations));
        return { success: type != null, addedMeasureType: type ?? undefined };
    } catch (e) {
        return { message: e instanceof Error ? e.message : 'Une erreur est survenue.' };
    }
};

export default addMeasureTypeAction;